/**
 * Back-office club & user administration contracts (TW-3436).
 *
 * The Zod schemas here are the single source of truth for request validation: the API
 * validates bodies with them (via the ZodValidationPipe) and the web forms validate against
 * the very same objects — one rule, two enforcement points (architecture §4.1). Response
 * DTO shapes live alongside so both sides import one definition.
 */
import { z } from "zod";
import { LANGUAGES } from "./constants";

// ── Lifecycle enums mirrored from the Prisma schema (data-model §9) ───────────
export const CLUB_STATUSES = ["active", "suspended"] as const;
export type ClubStatus = (typeof CLUB_STATUSES)[number];

export const USER_STATUSES = ["active", "disabled"] as const;
export type UserAccountStatus = (typeof USER_STATUSES)[number];

const languageSchema = z.enum(LANGUAGES);
// Lowercase kebab-case — matches the unique `club.slug` used for tenancy/routing.
const slugSchema = z
  .string()
  .trim()
  .min(2)
  .max(64)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "slug must be lowercase kebab-case");

// Short code: 2–4 alphanumeric chars, stored uppercase. Shown on the club and destined for
// trip/invoice numbers. Auto-suggested from the club name on create (see `suggestShortCode`).
const shortCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9]{2,4}$/, "short code must be 2–4 letters or digits");

/**
 * Suggest a short code from a club name: the first letter of each word (e.g. "RC Polo Barcelona"
 * → "RPB"), capped at 4. Falls back to the first letters of the name when that yields fewer than
 * two characters (single-word names). Always returns at least two characters. Used to pre-fill the
 * create form; the operator can override it.
 */
export function suggestShortCode(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const initials = words.map((w) => w[0] ?? "").join("");
  const alnum = name.replace(/[^A-Za-z0-9]/g, "");
  const base = (initials.length >= 2 ? initials : alnum).toUpperCase().slice(0, 4);
  return (base.length >= 2 ? base : `${base}XX`.slice(0, 2)) || "CLB";
}

// Primary club contact (name/phone/email) — all optional. Forms send "" when blank; the API
// stores that as NULL. The email accepts an empty string so a blank field doesn't fail validation.
const optionalContactText = (max: number) => z.string().trim().max(max).optional();
const optionalContactEmail = z
  .preprocess(
    (v) => (typeof v === "string" ? v.trim() : v),
    z.union([z.literal(""), z.email().max(200)]),
  )
  .optional();

// ── Clubs ─────────────────────────────────────────────────────────────────────
export const createClubSchema = z.object({
  name: z.string().trim().min(2).max(120),
  slug: slugSchema,
  // Optional on create: the server derives it from the name when the operator leaves it blank.
  shortCode: shortCodeSchema.optional(),
  primaryContact: optionalContactText(120),
  contactPhone: optionalContactText(40),
  contactEmail: optionalContactEmail,
  defaultLanguage: languageSchema.default("es"),
  status: z.enum(CLUB_STATUSES).optional(),
});
export type CreateClubInput = z.infer<typeof createClubSchema>;

export const updateClubSchema = z
  .object({
    name: z.string().trim().min(2).max(120).optional(),
    shortCode: shortCodeSchema.optional(),
    primaryContact: optionalContactText(120),
    contactPhone: optionalContactText(40),
    contactEmail: optionalContactEmail,
    defaultLanguage: languageSchema.optional(),
    status: z.enum(CLUB_STATUSES).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: "no fields to update" });
export type UpdateClubInput = z.infer<typeof updateClubSchema>;

// ── Users (per club) ────────────────────────────────────────────────────────────
// Names are collected as separate first/last fields in the invite forms; the API combines them
// into the single `user.name` column the DB stores (see `fullName`).
const firstNameSchema = z.string().trim().min(1).max(60);
const lastNameSchema = z.string().trim().min(1).max(60);

/** Combine the separate invite name fields into the single `user.name` the DB stores. */
export function fullName(firstName: string, lastName: string): string {
  return `${firstName.trim()} ${lastName.trim()}`.trim();
}

// Back-office invite of a club user. Club roles are hidden everywhere (the club portal and here):
// every club user is a peer holding `user.manage`, so there is no `roleId` — the server assigns the
// default club role (see UsersService.invite / defaultClubRoleId). `language` is optional; it falls
// back to the club's default.
export const inviteUserSchema = z.object({
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  email: z.email().max(200),
  language: languageSchema.optional(),
});
export type InviteUserInput = z.infer<typeof inviteUserSchema>;

/**
 * Back-office invite of a club user (redesigned modal): just a full name + email. Roles are no
 * longer chosen in the UI — the server assigns the default club role, exactly like the club's own
 * team self-service invite ({@link inviteClubMemberSchema}). Language defaults to the club's.
 */
export const inviteClubUserSchema = z.object({
  name: z.string().trim().min(2).max(120),
  email: z.email().max(200),
  language: languageSchema.optional(),
});
export type InviteClubUserInput = z.infer<typeof inviteClubUserSchema>;

export const updateUserSchema = z
  .object({
    status: z.enum(USER_STATUSES).optional(),
    roleId: z.uuid().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: "no fields to update" });
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

// ── Club team self-service ────────────────────────────────────────────────────
// A club user inviting another member to their *own* club. Identical to inviteUserSchema but
// without `roleId`: club roles are hidden in the club portal, so the server assigns the default
// club role itself (the club id comes from the authenticated caller, never the request body).
export const inviteClubMemberSchema = z.object({
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  email: z.email().max(200),
  language: languageSchema.optional(),
});
export type InviteClubMemberInput = z.infer<typeof inviteClubMemberSchema>;

// ── Back-office team ──────────────────────────────────────────────────────────
// A back-office admin inviting a TC teammate (Settings → Team in the back office). Unlike club
// invites, back-office roles ARE user-facing (Admin / Staff / Read-Only differ meaningfully), so
// the invite carries a required `roleId`; the server verifies it references a BACK_OFFICE role.
export const inviteBackofficeUserSchema = z.object({
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  email: z.email().max(200),
  roleId: z.uuid(),
  language: languageSchema.optional(),
});
export type InviteBackofficeUserInput = z.infer<typeof inviteBackofficeUserSchema>;

// ── Invite acceptance (public set-password) ──────────────────────────────────────
export const acceptInviteSchema = z.object({
  password: z.string().min(8).max(128),
});
export type AcceptInviteInput = z.infer<typeof acceptInviteSchema>;

// ── Response DTOs ─────────────────────────────────────────────────────────────
export interface ClubDto {
  id: string;
  name: string;
  slug: string;
  /** Short identifier (2–4 chars, uppercase) shown on the club and used in trip/invoice numbers. */
  shortCode: string;
  /** Primary point of contact at the club. Empty string when not set. */
  primaryContact: string;
  contactPhone: string;
  contactEmail: string;
  status: ClubStatus;
  defaultLanguage: (typeof LANGUAGES)[number];
  userCount: number;
  /** Total trips ever created for this club (all statuses). */
  tripCount: number;
  /** Trips not yet Completed or Cancelled — the club's live workload. */
  openTripCount: number;
  createdAt: string;
}

export interface RoleDto {
  id: string;
  name: string;
}

export interface ClubUserDto {
  id: string;
  name: string;
  email: string;
  status: UserAccountStatus;
  language: (typeof LANGUAGES)[number];
  roles: RoleDto[];
  /** True until the invite is accepted (no usable credential yet). */
  pending: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

/** What the public set-password page shows before the user picks a password. */
export interface InviteInfoDto {
  name: string;
  email: string;
}
